/* Code → label maps + filter option lists + per-layer action sets (spec §9). */

export const LAYER_LABELS: Record<string, string> = {
    signal: 'Signal',
    strategic: 'Strategic',
    activation: 'Activation',
};

export const OUTCOME_LABELS: Record<string, string> = {
    hiring: 'Hiring',
    mentorship: 'Career Guidance',
    donor: 'Donor',
    am: 'Admissions & Marketing',
    at: 'Academics & Thought Leadership',
    ra: 'Ranking & Accreditation',
};

export const STAGE_LABELS: Record<string, string> = {
    identified: 'Identified',
    validated: 'Verified',
    warmed: 'Warmed',
    engaged: 'Engaged',
    qualified: 'Qualified',
    contribution: 'Contributed',
    dormant: 'Dormant',
    denied: 'Denied',
};
export const STAGES = Object.keys(STAGE_LABELS);

// Per-outcome stage descriptions shown as hover tooltips on stage badges.
export const DONOR_STAGE_DESCRIPTIONS: Record<string, string> = {
    identified:   'Identified as a potential donor — contactability not yet established (no verified email or phone).',
    validated:    'Identified as a potential donor and contactable via at least one verified channel.',
    warmed:       'Responding to general campaigns and outreach — no Fundraise-specific engagement yet.',
    engaged:      'Actively interacting with Fundraise-specific campaigns or outreach in the last 6 months.',
    qualified:    'Has said willing to contribute to at least one fundraise pathway — no commitment marked Done yet.',
    contribution: 'Has already contributed to at least one fundraise commitment pathway.',
    dormant:      'Identified as a potential donor but has stopped responding to any campaigns or outreach for 6+ months.',
    denied:       'Has explicitly declined all fundraise commitment pathways — no willing or done at any level.',
};

export const AT_STAGE_DESCRIPTIONS: Record<string, string> = {
    identified:   'Identified as a potential Thought Leader — contactability not yet established (no genuine verified email and phone).',
    validated:    'Identified as a potential Thought Leader and contactable via at least one verified channel (genuine email required along with verified contact).',
    warmed:       'Identified as a potential Thought Leader and already responding to general campaigns and outreach — no Thought Leadership-specific engagement yet.',
    engaged:      'Actively interacting with Thought Leadership-specific campaigns or outreach in the last 6 months.',
    qualified:    'Has shown willingness to contribute to at least one Academic & Thought Leadership pathway — no commitment marked Done yet.',
    contribution: 'Has already contributed to at least one Academic & Thought Leadership commitment pathway.',
    dormant:      'Identified as a potential Thought Leader but has stopped responding to any campaigns or outreach for 6+ months.',
    denied:       'Has explicitly declined all AT pathways — no willing or done at any level. Automatically re-evaluated on the next snapshot run when willing is updated.',
};

export const HIRING_STAGE_DESCRIPTIONS: Record<string, string> = {
    identified:   'Identified as a potential Jobs & Placements contributor — contactability not yet established (no genuine verified email and phone).',
    validated:    'Identified as a potential Jobs & Placements contributor and contactable via at least one verified channel.',
    warmed:       'Responding to general campaigns and outreach — no Recruitment-specific engagement yet.',
    engaged:      'Actively interacting with Jobs & Placements-specific campaigns or outreach in the last 6 months.',
    qualified:    'Has shown willingness to contribute to at least one Jobs & Placements pathway — no commitment marked Done yet.',
    contribution: 'Has already contributed to at least one Jobs & Placements commitment pathway.',
    dormant:      'Identified as a potential recruiter but has stopped responding to any campaigns or outreach for 6+ months.',
    denied:       'Has explicitly declined all Jobs & Placements pathways — no willing or done at any level. Automatically re-evaluated on the next snapshot run when willing is updated.',
};

export const MENTORSHIP_STAGE_DESCRIPTIONS: Record<string, string> = {
    identified:   'The ability or willingness signals have identified these users as potential targets for Career Guidance & Mentorship, however their contactability is yet to be established.',
    validated:    'The ability or willingness signals have identified these users as potential targets for Career Guidance & Mentorship, and they are contactable via at least one channel.',
    warmed:       'The ability or willingness signals have identified these users as potential targets for Career Guidance & Mentorship, and they are already responding to general campaigns and outreach efforts.',
    engaged:      'These users are interacting with and responding to Career Guidance & Mentorship specific campaigns and outreach efforts.',
    qualified:    'These users have shown willingness to contribute towards any of the Career Guidance commitment pathways.',
    contribution: 'These users have already contributed towards any of the Career Guidance commitment pathways.',
    dormant:      'The ability or willingness signals have identified these users as potential targets for Career Guidance, but they have stopped responding to any of campaigns and outreach efforts.',
    denied:       'These users were identified to have the ability to contribute, but they have explicitly mentioned that they don’t want to contribute towards any of Career Guidance commitment pathways',
};

// Lookup helper — returns the outcome-specific description map or null for outcomes without one.
export const getStageDescriptions = (outcome: string | null): Record<string, string> | null => {
    if (outcome === 'donor')   return DONOR_STAGE_DESCRIPTIONS;
    if (outcome === 'at')      return AT_STAGE_DESCRIPTIONS;
    if (outcome === 'hiring')  return HIRING_STAGE_DESCRIPTIONS;
    if (outcome === 'mentorship') return MENTORSHIP_STAGE_DESCRIPTIONS;
    return null;
};

export const SIGNAL_LABELS: Record<string, string> = {
    cxo_leadership: 'CXO / Leadership Pool',
    entrepreneur: 'Claimed Entrepreneurs Pool',
    premium_company: 'Premium Companies Pool',
    domain_experts: 'Domain Experts Pool',
    active_alumni: 'Active Alumni Pool',
    dormant_alumni: 'Dormant / Needs Reactivation Pool',
};

const STRATEGIC_SUFFIX: Record<string, string> = { core: 'Core', conversion: 'Conversion', breakthrough: 'Breakthrough', reactivation: 'Reactivation' };

/** Resolve a watchlist_code to its human-readable label (signal / strategic / activation). */
export const watchlistCodeLabel = (code: string): string => {
    if (SIGNAL_LABELS[code]) return SIGNAL_LABELS[code];

    const strategic = code.match(/^(.*)_(core|conversion|breakthrough|reactivation)$/);
    if (strategic) {
        const [, outcome, suffix] = strategic;
        if (OUTCOME_LABELS[outcome] && STRATEGIC_SUFFIX[suffix]) {
            return `${OUTCOME_LABELS[outcome]} · ${STRATEGIC_SUFFIX[suffix]}`;
        }
    }

    const activation = code.match(/^activation_(.+)_(.+)$/);
    if (activation) {
        const [, outcome, stage] = activation;
        if (OUTCOME_LABELS[outcome] && STAGE_LABELS[stage]) {
            return `${OUTCOME_LABELS[outcome]} · ${STAGE_LABELS[stage]}`;
        }
    }

    return code;
};

// watchlist_code options for the filter dropdown, by layer.
export const watchlistCodeOptions = (layer: string | null | undefined): { value: string; label: string }[] => {
    if (layer === 'signal') return Object.entries(SIGNAL_LABELS).map(([value, label]) => ({ value, label }));
    if (layer === 'strategic') {
        const out: { value: string; label: string }[] = [];
        Object.keys(OUTCOME_LABELS).filter((o) => o !== 'ra').forEach((o) =>
            Object.entries(STRATEGIC_SUFFIX).forEach(([s, sl]) => out.push({ value: `${o}_${s}`, label: `${OUTCOME_LABELS[o]} · ${sl}` }))
        );
        return out;
    }
    if (layer === 'activation') {
        const out: { value: string; label: string }[] = [];
        Object.keys(OUTCOME_LABELS).forEach((o) =>
            STAGES.forEach((st) => out.push({ value: `activation_${o}_${st}`, label: `${OUTCOME_LABELS[o]} · ${STAGE_LABELS[st]}` }))
        );
        return out;
    }
    return [];
};

// HIGH / MODERATE / LOW → High / Moderate / Low for the chip label.
const titleCase = (v: string) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v);

// The dimensions shown as removable filter chips, in display order.
export const CHIP_DIMENSIONS: { key: string; label: string; format?: (v: string) => string }[] = [
    { key: 'layer', label: 'Layer', format: (v) => LAYER_LABELS[v] || v },
    { key: 'watchlist_code', label: 'Watchlist', format: watchlistCodeLabel },
    { key: 'outcome', label: 'Outcome', format: (v) => OUTCOME_LABELS[v] || v },
    { key: 'stage', label: 'Stage', format: (v) => STAGE_LABELS[v] || v },
    { key: 'program', label: 'Program' },
    { key: 'batch_year', label: 'Batch' },
    { key: 'alma_mater_pride', label: 'Alma Mater Pride', format: titleCase },
    { key: 'marketability', label: 'Marketability', format: titleCase },
    { key: 'vibrancy', label: 'Vibrancy', format: titleCase },
    { key: 'connected', label: 'Connected', format: titleCase },
    { key: 'recognized', label: 'Recognized', format: titleCase },
    { key: 'engaged', label: 'Engaged', format: titleCase },
    { key: 'pitch_ready', label: 'Pitch Ready' },
    { key: 'reachout_phase', label: 'Phase', format: (v) => (v === 'not_assigned' ? 'Not assigned' : v) },
    { key: 'date_from', label: 'From' },
    { key: 'date_to', label: 'To' },
    { key: 'search', label: 'Search' },
];
